import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Card,
  Flex,
  Heading,
  Text,
  View,
  Badge,
  Divider,
  Alert,
  Loader,
  Grid
} from '@aws-amplify/ui-react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import corsProxy from '../../utils/corsProxy';
import { obfuscateEmail, getStatusBadgeVariation } from '../../utils/helpers';

const POLL_INTERVAL = 30000; // 30 seconds

const ParticipantDashboard = ({ user, projectId: routeProjectId }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(routeProjectId || null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchParticipantProjects();
  }, []);

  // Deep link support
  useEffect(() => {
    if (routeProjectId) {
      setSelectedProjectId(routeProjectId);
    }
  }, [routeProjectId]);

  const fetchParticipantProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const userProjects = currentUser.attributes['custom:projects'] || '';

      if (!userProjects) {
        setProjects([]);
        setLoading(false);
        return;
      }

      const projectIds = userProjects.split(',').map(p => p.trim()).filter(Boolean);

      const projectResults = await Promise.all(
        projectIds.map(async (pid) => {
          try {
            const response = await corsProxy.get(`/projects/${pid}`);
            return response.data || response;
          } catch (err) {
            console.error(`Error fetching project ${pid}:`, err);
            return null;
          }
        })
      );

      setProjects(projectResults.filter(Boolean));
    } catch (err) {
      console.error('Error fetching participant projects:', err);
      setError('Failed to load your projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const userEmail = user.attributes?.email || user.username;

  const getUserTurnInfo = (project) => {
    if (!project?.participantDetails) return { status: 'unknown' };
    const participant = project.participantDetails.find(p => p.email === userEmail);
    if (!participant) return { status: 'not-found' };

    if (participant.selectedNumber) {
      return { status: 'selected', number: participant.selectedNumber };
    }

    if (project.status !== 'Active') {
      return { status: 'waiting', reason: project.status === 'Draft' ? 'Project not started' : 'Project completed' };
    }

    const sorted = [...project.participantDetails].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentTurnIndex = sorted.findIndex(p => !p.selectedNumber && p.status !== 'Skipped');
    const userIndex = sorted.findIndex(p => p.email === userEmail);

    if (participant.status === 'Skipped') {
      return { status: 'skipped' };
    }

    if (currentTurnIndex === userIndex) {
      return { status: 'your-turn' };
    }

    if (currentTurnIndex < 0) {
      return { status: 'waiting', reason: 'All turns complete' };
    }

    // Count active (non-skipped, non-selected) participants ahead
    const ahead = sorted.slice(currentTurnIndex, userIndex).filter(p => !p.selectedNumber && p.status !== 'Skipped').length;
    return { status: 'waiting', position: ahead };
  };

  const handleSelectProject = (project) => {
    const pid = project.id || project.projectId;
    setSelectedProjectId(pid);
    navigate(`/projects/${pid}`);
  };

  const handleBack = () => {
    setSelectedProjectId(null);
    navigate('/');
  };

  if (loading) {
    return (
      <Flex direction="column" alignItems="center" padding="2rem">
        <Loader size="large" />
        <Text marginTop="1rem">Loading your projects...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert variation="error" isDismissible onDismiss={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  const selectedProject = selectedProjectId
    ? projects.find(p => (p.id || p.projectId) === selectedProjectId)
    : null;

  if (selectedProject) {
    return (
      <ProjectDetailView
        project={selectedProject}
        user={user}
        userEmail={userEmail}
        onBack={handleBack}
        onRefresh={fetchParticipantProjects}
      />
    );
  }

  return (
    <Flex direction="column" gap="1.5rem">
      <Flex justifyContent="space-between" alignItems="center">
        <Heading level={3}>My Projects</Heading>
        <Button variation="primary" onClick={fetchParticipantProjects}>
          Refresh
        </Button>
      </Flex>

      {projects.length === 0 ? (
        <Card padding="2rem" textAlign="center">
          <Heading level={4}>No Projects Assigned</Heading>
          <Text>You haven't been assigned to any projects yet. Contact your administrator to get started.</Text>
        </Card>
      ) : (
        <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap="1rem">
          {projects.map((project) => {
            const turnInfo = getUserTurnInfo(project);
            return (
              <ProjectCard
                key={project.id}
                project={project}
                turnInfo={turnInfo}
                onSelect={() => handleSelectProject(project)}
              />
            );
          })}
        </Grid>
      )}
    </Flex>
  );
};

// --- Project Card ---
const ProjectCard = ({ project, turnInfo, onSelect }) => {
  const isYourTurn = turnInfo.status === 'your-turn';

  return (
    <Card
      variation="outlined"
      padding="1rem"
      className={isYourTurn ? 'your-turn-card' : ''}
      style={isYourTurn ? { border: '2px solid #ff6b35', boxShadow: '0 0 12px rgba(255, 107, 53, 0.2)' } : {}}
    >
      <Flex direction="column" gap="0.5rem">
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Heading level={5}>{project.name}</Heading>
          <Badge variation={getStatusBadgeVariation(project.status)}>
            {project.status}
          </Badge>
        </Flex>

        {project.description && (
          <Text fontSize="0.9rem" color="gray">{project.description}</Text>
        )}

        <Divider marginTop="0.5rem" marginBottom="0.5rem" />

        <Text fontSize="0.8rem">
          <strong>Number Range:</strong> {project.minNumber} - {project.maxNumber}
        </Text>

        <TurnStatusBadge turnInfo={turnInfo} />

        <Button
          variation={isYourTurn ? 'primary' : 'link'}
          size="small"
          marginTop="0.5rem"
          onClick={onSelect}
        >
          {isYourTurn ? 'Select Your Number' : 'View Details'}
        </Button>
      </Flex>
    </Card>
  );
};

// --- Turn Status Badge ---
const TurnStatusBadge = ({ turnInfo }) => {
  switch (turnInfo.status) {
    case 'your-turn':
      return (
        <Flex alignItems="center" gap="0.5rem">
          <Badge variation="warning" style={{ fontSize: '0.9rem', padding: '0.25rem 0.75rem' }}>
            Your Turn!
          </Badge>
        </Flex>
      );
    case 'skipped':
      return <Badge variation="error">Skipped (timed out)</Badge>;
    case 'selected':
      return (
        <Badge variation="success">Selected: {turnInfo.number}</Badge>
      );
    case 'waiting':
      return (
        <Flex alignItems="center" gap="0.5rem">
          <Badge variation="info">Waiting</Badge>
          {turnInfo.position && (
            <Text fontSize="0.8rem" color="gray">
              {turnInfo.position === 1 ? 'You\'re next!' : `${turnInfo.position} people ahead`}
            </Text>
          )}
          {turnInfo.reason && (
            <Text fontSize="0.8rem" color="gray">{turnInfo.reason}</Text>
          )}
        </Flex>
      );
    default:
      return <Badge>Unknown</Badge>;
  }
};

// --- Progress Bar ---
const SelectionProgress = ({ participantDetails }) => {
  if (!participantDetails?.length) return null;

  const total = participantDetails.length;
  const completed = participantDetails.filter(p => p.selectedNumber).length;
  const skipped = participantDetails.filter(p => p.status === 'Skipped').length;
  const pct = Math.round(((completed + skipped) / total) * 100);

  return (
    <Flex direction="column" gap="0.25rem">
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize="0.85rem" fontWeight="600">Selection Progress</Text>
        <Text fontSize="0.85rem" color="gray">
          {completed} selected{skipped > 0 ? `, ${skipped} skipped` : ''} of {total} ({pct}%)
        </Text>
      </Flex>
      <View
        style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#e5e7eb',
          borderRadius: '4px',
          overflow: 'hidden'
        }}
      >
        <View
          style={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: pct === 100 ? '#22c55e' : '#ff6b35',
            borderRadius: '4px',
            transition: 'width 0.5s ease'
          }}
        />
      </View>
    </Flex>
  );
};

// --- Project Detail View ---
const ProjectDetailView = ({ project: initialProject, user, userEmail, onBack, onRefresh }) => {
  const [project, setProject] = useState(initialProject);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const pollRef = useRef(null);

  // Auto-poll when project is active
  useEffect(() => {
    const isActive = project.status === 'Active';
    const userDone = project.participantDetails?.find(p => p.email === userEmail)?.selectedNumber;

    if (isActive && !userDone) {
      pollRef.current = setInterval(async () => {
        try {
          const response = await corsProxy.get(`/projects/${project.id || project.projectId}`);
          const updated = response.data || response;
          setProject(updated);
          setLastRefresh(Date.now());
        } catch (err) {
          console.error('Poll error:', err);
        }
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [project.id, project.projectId, project.status, userEmail]);

  const participantDetails = project.participantDetails || [];
  const sorted = [...participantDetails].sort((a, b) => (a.order || 0) - (b.order || 0));
  const userParticipant = sorted.find(p => p.email === userEmail);
  const currentTurn = sorted.find(p => !p.selectedNumber && p.status !== 'Skipped');
  const isUserTurn = project.status === 'Active' && currentTurn?.email === userEmail && !userParticipant?.selectedNumber && userParticipant?.status !== 'Skipped';

  const takenNumbers = new Set(participantDetails.filter(p => p.selectedNumber).map(p => p.selectedNumber));
  const min = project.minNumber || 1;
  const max = project.maxNumber || 100;
  const availableNumbers = [];
  for (let i = min; i <= max; i++) {
    if (!takenNumbers.has(i)) availableNumbers.push(i);
  }

  const handleNumberSelection = async () => {
    if (!selectedNumber || !isUserTurn) return;

    try {
      setSubmitting(true);
      setError(null);

      const response = await corsProxy.post(`/projects/${project.id}/select-number`, {
        selectedNumber: selectedNumber
      });

      setSuccess(`You selected number ${selectedNumber}!`);
      const updated = response.project || response;
      if (updated.participantDetails) {
        setProject(updated);
      }

      setTimeout(() => {
        onRefresh();
      }, 2000);
    } catch (err) {
      console.error('Error selecting number:', err);
      const msg = err?.response?.data?.message || err.message || 'Failed to select number';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualRefresh = async () => {
    try {
      const response = await corsProxy.get(`/projects/${project.id || project.projectId}`);
      setProject(response.data || response);
      setLastRefresh(Date.now());
    } catch (err) {
      console.error('Refresh error:', err);
    }
  };

  return (
    <Flex direction="column" gap="1.5rem">
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center" wrap="wrap" gap="0.5rem">
        <Heading level={3}>{project.name}</Heading>
        <Flex gap="0.5rem" alignItems="center">
          <Text fontSize="0.75rem" color="gray">
            Updated {new Date(lastRefresh).toLocaleTimeString()}
          </Text>
          <Button variation="link" size="small" onClick={handleManualRefresh}>Refresh</Button>
          <Button variation="link" onClick={onBack}>← Back</Button>
        </Flex>
      </Flex>

      {error && (
        <Alert variation="error" isDismissible onDismiss={() => setError(null)}>{error}</Alert>
      )}
      {success && (
        <Alert variation="success" isDismissible onDismiss={() => setSuccess(null)}>{success}</Alert>
      )}

      {/* Your Turn Banner */}
      {isUserTurn && !success && (
        <Card
          padding="1.5rem"
          style={{
            background: 'linear-gradient(135deg, #ff6b35, #ffa500)',
            color: 'white',
            borderRadius: '16px'
          }}
        >
          <Flex direction="column" alignItems="center" gap="0.5rem">
            <Heading level={4} style={{ color: 'white' }}>It's Your Turn!</Heading>
            <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
              Select a number from {min} to {max} ({availableNumbers.length} available)
            </Text>
          </Flex>
        </Card>
      )}

      {/* Already Selected Banner */}
      {userParticipant?.selectedNumber && (
        <Card padding="1.5rem" style={{ background: '#f0fdf4', borderRadius: '16px' }}>
          <Flex direction="column" alignItems="center" gap="0.5rem">
            <Heading level={4}>You Selected</Heading>
            <Text style={{ fontSize: '2.5rem', fontWeight: '700', color: '#22c55e' }}>
              {userParticipant.selectedNumber}
            </Text>
          </Flex>
        </Card>
      )}

      {/* Waiting Banner */}
      {!isUserTurn && !userParticipant?.selectedNumber && project.status === 'Active' && (
        <Card padding="1.5rem" style={{ background: '#eff6ff', borderRadius: '16px' }}>
          <Flex direction="column" alignItems="center" gap="0.5rem">
            <Heading level={4}>Waiting for Your Turn</Heading>
            {currentTurn && (() => {
              const userIndex = sorted.findIndex(p => p.email === userEmail);
              const currentIndex = sorted.findIndex(p => p.email === currentTurn.email);
              const ahead = userIndex - currentIndex;
              return (
                <Text color="gray">
                  {ahead === 1
                    ? "You're next!"
                    : `${ahead} ${ahead === 1 ? 'person' : 'people'} ahead of you`
                  }
                </Text>
              );
            })()}
            <Text fontSize="0.8rem" color="gray">This page auto-refreshes every 30 seconds</Text>
          </Flex>
        </Card>
      )}

      {/* Progress */}
      <Card padding="1rem">
        <SelectionProgress participantDetails={sorted} />
      </Card>

      {/* Number Selection Grid */}
      {isUserTurn && !success && (
        <Card padding="1.5rem">
          <Flex direction="column" gap="1rem">
            <Heading level={4}>Choose Your Number</Heading>

            <Grid templateColumns="repeat(auto-fill, minmax(52px, 1fr))" gap="0.4rem">
              {availableNumbers.map(number => (
                <Button
                  key={number}
                  variation={selectedNumber === number ? 'primary' : 'outlined'}
                  size="small"
                  onClick={() => setSelectedNumber(number)}
                  style={selectedNumber === number ? { transform: 'scale(1.1)' } : {}}
                >
                  {number}
                </Button>
              ))}
            </Grid>

            {selectedNumber && (
              <Flex
                gap="1rem"
                alignItems="center"
                justifyContent="center"
                padding="1rem"
                style={{ background: '#fff7ed', borderRadius: '12px' }}
              >
                <Text fontSize="1.1rem">
                  Your pick: <strong style={{ fontSize: '1.3rem' }}>{selectedNumber}</strong>
                </Text>
                <Button
                  variation="primary"
                  onClick={handleNumberSelection}
                  isLoading={submitting}
                  loadingText="Confirming..."
                >
                  Confirm Selection
                </Button>
                <Button variation="link" onClick={() => setSelectedNumber(null)}>
                  Change
                </Button>
              </Flex>
            )}
          </Flex>
        </Card>
      )}

      {/* Participant List */}
      <Card padding="1.5rem">
        <Heading level={4} marginBottom="1rem">Participants</Heading>
        <Flex direction="column" gap="0.5rem">
          {sorted.map((participant, index) => {
            const isCurrent = currentTurn?.email === participant.email && project.status === 'Active';
            const isMe = participant.email === userEmail;
            const done = !!participant.selectedNumber;

            return (
              <Flex
                key={participant.email}
                justifyContent="space-between"
                alignItems="center"
                padding="0.75rem 1rem"
                style={{
                  background: isMe ? '#fff7ed' : isCurrent ? '#fefce8' : done ? '#f0fdf4' : '#f8fafc',
                  borderRadius: '8px',
                  border: isMe ? '1px solid #ff6b35' : '1px solid transparent'
                }}
              >
                <Flex alignItems="center" gap="0.75rem">
                  <Text
                    fontSize="0.8rem"
                    fontWeight="600"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: done ? '#22c55e' : isCurrent ? '#f59e0b' : '#d1d5db',
                      color: 'white'
                    }}
                  >
                    {done ? '✓' : index + 1}
                  </Text>
                  <Flex direction="column">
                    <Text fontWeight={isMe ? '600' : '400'}>
                      {participant.name}{isMe ? ' (you)' : ''}
                    </Text>
                    <Text fontSize="0.75rem" color="gray">
                      {obfuscateEmail(participant.email, userEmail)}
                    </Text>
                  </Flex>
                </Flex>

                <Flex alignItems="center" gap="0.5rem">
                  {done ? (
                    <Badge variation="success">{participant.selectedNumber}</Badge>
                  ) : participant.status === 'Skipped' ? (
                    <Badge variation="error">Skipped</Badge>
                  ) : isCurrent ? (
                    <Badge variation="warning">Selecting...</Badge>
                  ) : (
                    <Badge variation="info">Waiting</Badge>
                  )}
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </Card>

      {/* Project Info */}
      <Card padding="1rem">
        <Flex direction="column" gap="0.5rem">
          <Text fontSize="0.85rem"><strong>Status:</strong> {project.status}</Text>
          <Text fontSize="0.85rem"><strong>Number Range:</strong> {min} - {max}</Text>
          {project.description && (
            <Text fontSize="0.85rem"><strong>Description:</strong> {project.description}</Text>
          )}
        </Flex>
      </Card>
    </Flex>
  );
};

export default ParticipantDashboard;
